/**
 * @file src/pages/inside/owner/style.js
 * @description Styles for Owner Pages
 * @version 1.0.0
 * @returns {React.Component} Dashboard component
 */

import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Card, Table } from 'antd';

export const DashboardContainer = styled.div`
    padding: ${({ theme }) => theme.spacing.xl} 60px ${({ theme }) => theme.spacing.xl} 30px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    background-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f0f0f0';
    }};
    min-height: 100vh;
    box-sizing: border-box;
    position: relative;

    @media (max-width: 1024px) {
        padding: ${({ theme }) => theme.spacing.xl} 24px ${({ theme }) => theme.spacing.xl} 20px;
    }

    @media (max-width: 768px) {
        padding: ${({ theme }) => theme.spacing.lg} 16px ${({ theme }) => theme.spacing.lg} 16px;
    }

    @media (max-width: 480px) {
        padding: ${({ theme }) => theme.spacing.md} 12px ${({ theme }) => theme.spacing.md} 12px;
    }
`;

export const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.md};
`;

export const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 600;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    margin: 0;
    font-family: var(--font-family-lufga);
`;

export const PageHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
`;

export const KPICardsGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ $columns }) => {
        if ($columns === 4) {
            return 'repeat(4, 1fr)';
        }
        return $columns ? `repeat(${$columns}, 1fr)` : 'repeat(2, 1fr)';
    }};
    grid-template-rows: ${({ $rows }) => $rows ? `repeat(${$rows}, 1fr)` : '1fr 1fr'};
    gap: 16px;
    width: 100%;
    height: ${({ $height }) => $height || '450px'};
    align-self: start;
    
    & > * {
        height: ${({ $height }) => $height === 'auto' ? 'auto' : '100%'};
        min-height: 0;
        overflow: hidden;
    }

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        height: auto;
        gap: 12px;
        
        & > * {
            height: auto;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }

    @media (max-width: 480px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }
`;

export const FirstRowContainer = styled.div`
    display: grid;
    grid-template-columns: 63% 37%;
    gap: 16px;
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    align-items: start;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: visible !important; /* Allow chart segments to extend on hover */

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 16px;
        margin-bottom: ${({ theme }) => theme.spacing.lg};
    }

    @media (max-width: 768px) {
        gap: 12px;
        margin-bottom: ${({ theme }) => theme.spacing.md};
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    min-width: 0;
    overflow: hidden;

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const ChartsColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    overflow: visible !important; /* Allow chart segments to extend on hover */

    & > *:nth-child(1) {
        height: 450px;
        min-height: 450px;
        overflow: visible !important;
    }
    
    & > *:nth-child(2) {
        height: 463px;
        min-height: 463px;
    }

    @media (max-width: 1024px) {
        width: 100%;
        max-width: 100%;
        gap: 16px;
        
        & > *:nth-child(1),
        & > *:nth-child(2) {
            height: auto;
            min-height: auto;
        }
    }

    @media (max-width: 768px) {
        gap: 12px;
    }

    @media (max-width: 480px) {
        gap: 10px;
    }
`;

export const SecondRowContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        grid-template-columns: 1fr;
    }
`;

export const KPICard = styled(Card)`
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }} !important;
    border-radius: 40px;
    border: none !important;
    box-shadow: none !important;
    height: 100%;
    min-height: 0;
    transition: box-shadow 0.2s ease;
    margin: 0;
    display: flex;
    flex-direction: column;

    &:hover {
        box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)';
    }};
    }

    .ant-card-body {
        padding: 22px;
        height: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
`;

export const KPICardContent = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 0;
`;

export const KPICardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-bottom: auto;
`;

export const KPICardBottomRow = styled.div`
    display: grid;
    grid-template-columns: 60% 40%;
    gap: 20px;
    align-items: stretch;
    flex: 1;
    min-height: 0;
    margin-top: auto;
`;

export const KPICardLeft = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
    height: 100%;
    position: relative;
`;

export const KPICardIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#2C2C2C' : '#F5F5F5';
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 16px;

    img {
        width: 16px;
        height: 16px;
        object-fit: contain;
        filter: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'brightness(0) invert(1)' : 'none';
    }};
    }
`;

export const KPICardTitle = styled.div`
    font-size: 16px;
    line-height: 1.5;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#666666';
    }};
    font-weight: 400;
    font-family: var(--font-family-figtree);
`;

export const KPICardValue = styled.div`
    font-size: 51px;
    line-height: 1.1;
    font-weight: 600;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#292929';
    }};
    font-family: var(--font-family-lufga);
    letter-spacing: -1px;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
`;

export const KPICardChange = styled.div`
    font-size: 14px;
    line-height: 1.5;
    color: ${({ $positive, $negative, theme }) => {
        if ($positive) return '#34C759';
        if ($negative) return '#FF383C';
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#666666';
    }};
    margin-bottom: 0;
    font-weight: 400;
    font-family: var(--font-family-figtree);
    display: flex;
    align-items: center;
    gap: 5px;
    position: absolute;
    bottom: 0;
    left: 0;

    .anticon {
        font-size: 12px;
        color: inherit;
    }
`;

export const KPICardChart = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
    min-width: 0;
    overflow: hidden;
`;

export const MiniBarChart = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    height: 100%;
    width: 100%;
    gap: 12px;
`;

export const MiniBarContainer = styled.div`
    position: relative;
    width: 14px;
    height: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    flex-shrink: 0;
`;

export const MiniBar = styled.div`
    position: relative;
    width: 14px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0;
`;

export const MiniBarGrey = styled.div`
    width: 100%;
    height: ${({ $greyPercentage }) => {
        const greyPercentage = $greyPercentage !== undefined ? Math.min(Math.max($greyPercentage, 0), 100) : 100;
        return `${greyPercentage}%`;
    }};
    background: #e9edf7;
    flex-shrink: 0;
    min-height: 0;
    border-radius: 50px;
    position: absolute;
    top: ${({ $greyPercentage }) => {
        const greyPercentage = $greyPercentage !== undefined ? Math.min(Math.max($greyPercentage, 0), 100) : 100;
        return `${100 - greyPercentage}%`;
    }};
    left: 0;
    right: 0;
`;

export const MiniBarRed = styled.div`
    width: 100%;
    height: ${({ $redPercentage }) => {
        const redPercentage = Math.min(Math.max($redPercentage, 0), 100);
        return `${redPercentage}%`;
    }};
    background: #F04A4A;
    flex-shrink: 0;
    min-height: ${({ $redPercentage }) => {
        const redPercentage = Math.min(Math.max($redPercentage, 0), 100);
        return redPercentage > 0 ? '14px' : '0';
    }};
    border-radius: 50px;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    transform: scaleY(0);
    transform-origin: bottom;
    animation: growUp 0.8s ease-out forwards;
    
    @keyframes growUp {
        to {
            transform: scaleY(1);
        }
    }
`;

export const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.xl};

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        grid-template-columns: 1fr;
    }
`;

export const ChartCard = styled(Card)`
    background: ${({ theme, $red, $dark }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($red) return '#F04A4A';
        if ($dark) return '#292929'; // Always dark background for Top Performing
        return isDark ? '#292929' : '#ffffff';
    }} !important;
    border-radius: 40px;
    border: ${({ theme, $red, $dark }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($red || $dark) return 'none';
        return `1px solid ${isDark ? '#2C2C2C' : '#E5E5E5'}`;
    }} !important;
    box-shadow: ${({ $red, $dark }) => ($red || $dark) ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
    height: ${({ $red, $totalRevenue }) => {
        if ($red) return 'auto';
        if ($totalRevenue) return '100%';
        return '100%';
    }};
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: visible !important; /* Allow chart segments to extend on hover */
    
    /* Revenue card - expandable width, fixed height */
    ${({ $red }) => $red && `
        /* Base height at 1920px */
        @media (min-width: 1920px) {
            height: 463px;
            width: 100%;
        }
        
        /* Scale proportionally for smaller screens */
        @media (max-width: 1919px) {
            height: calc(100vw * 463 / 1920);
            max-height: 463px;
            width: 100%;
        }
    `}
    
    /* Total Revenue card - expand to fit data on all screens */
    ${({ $totalRevenue }) => $totalRevenue && `
        /* Large screens - expand to fit content */
        @media (min-width: 1025px) {
            height: auto;
            min-height: 463px;
        }
        
        /* Tablet and below - expand to fit content */
        @media (max-width: 1024px) {
            height: auto;
            min-height: 300px;
        }

        @media (max-width: 480px) {
            min-height: 250px;
        }
    `}

    @media (max-width: 1024px) {
        border-radius: 32px;
    }

    @media (max-width: 768px) {
        border-radius: 24px;
    }

    @media (max-width: 480px) {
        border-radius: 20px;
    }

    .ant-card-body {
        padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '26px 30px';
        return $dark ? '20px' : '16px';
    }};
        height: ${({ $red, $totalRevenue }) => {
        if ($red) return '100%';
        if ($totalRevenue) return 'auto';
        return '100%';
    }};
        min-height: ${({ $totalRevenue }) => $totalRevenue ? '463px' : 'auto'};
        display: flex;
        flex-direction: column;
        justify-content: ${({ $totalRevenue }) => $totalRevenue ? 'space-between' : 'flex-start'};
        overflow: visible !important; /* Allow chart segments to extend on hover */

        @media (max-width: 1024px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '22px 24px';
        return $dark ? '18px' : '14px';
    }};
            min-height: ${({ $totalRevenue }) => $totalRevenue ? '300px' : 'auto'};
        }

        @media (max-width: 768px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '18px 20px';
        return $dark ? '16px' : '12px';
    }};
            min-height: ${({ $totalRevenue }) => $totalRevenue ? '250px' : 'auto'};
        }

        @media (max-width: 480px) {
            padding: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '16px 16px';
        return $dark ? '14px' : '10px';
    }};
            min-height: ${({ $totalRevenue }) => $totalRevenue ? '200px' : 'auto'};
        }
    }
`;

export const ChartCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.md};
`;

export const ChartCardTitle = styled.div`
    font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '14px';
        return $dark ? '24px' : '16px';
    }};
    font-weight: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '500';
        return $dark ? '600' : '700';
    }};
    color: ${({ theme, $red, $dark, $totalRevenue }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($totalRevenue) return '#A3AED0';
        if ($red) return '#ffffff';
        if ($dark) return '#ffffff'; // Always white text for Top Performing
        return isDark ? '#8A98A7' : '#666666';
    }};
    margin-top: 0 !important;
    margin-bottom: ${({ theme, $red, $dark, $totalRevenue }) => {
        if ($totalRevenue) return '0';
        return ($red || $dark) ? '14px' : theme.spacing.xs;
    }};

    @media (max-width: 1024px) {
        font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '13px';
        return $dark ? '22px' : '15px';
    }};
        margin-bottom: ${({ theme, $red, $dark, $totalRevenue }) => {
        if ($totalRevenue) return '0';
        return ($red || $dark) ? '12px' : theme.spacing.xs;
    }};
    }

    @media (max-width: 768px) {
        font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '12px';
        return $dark ? '20px' : '14px';
    }};
        margin-bottom: ${({ theme, $red, $dark, $totalRevenue }) => {
        if ($totalRevenue) return '0';
        return ($red || $dark) ? '10px' : theme.spacing.xs;
    }};
    }

    @media (max-width: 480px) {
        font-size: ${({ $dark, $totalRevenue }) => {
        if ($totalRevenue) return '11px';
        return $dark ? '18px' : '13px';
    }};
    }
`;

export const ChartCardValue = styled.div`
    font-size: ${({ $totalRevenue }) => $totalRevenue ? '34px' : '32px'};
    font-weight: 700;
    color: ${({ theme, $red, $totalRevenue }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($totalRevenue) return isDark ? '#ffffff' : '#292929';
        return $red ? '#ffffff' : (isDark ? '#ffffff' : '#292929');
    }};
    font-family: var(--font-family-lufga);
    padding-bottom: ${({ $totalRevenue }) => $totalRevenue ? '2px' : '0'};

    @media (max-width: 1024px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '30px' : '28px'};
    }

    @media (max-width: 768px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '26px' : '24px'};
    }

    @media (max-width: 480px) {
        font-size: ${({ $totalRevenue }) => $totalRevenue ? '22px' : '20px'};
    }
`;

export const ChartCardChange = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${({ $positive, $red, $totalRevenue }) => {
        if ($totalRevenue) {
            // Always green for positive, red for negative in Total Revenue
            return $positive ? '#52C41A' : '#F04A4A';
        }
        if ($red) return '#ffffff';
        return $positive ? '#52C41A' : '#F04A4A';
    }};
    display: flex;
    align-items: center;
    gap: 4px;
    
    img {
        width: 12px;
        height: 12px;
        object-fit: contain;
        filter: ${({ $positive, $totalRevenue }) => {
        if ($totalRevenue) {
            // Green for positive (up arrow), red for negative (down arrow)
            if ($positive) {
                return 'brightness(0) saturate(100%) invert(67%) sepia(95%) saturate(500%) hue-rotate(75deg) brightness(95%) contrast(95%)';
            } else {
                return 'brightness(0) saturate(100%) invert(25%) sepia(95%) saturate(5000%) hue-rotate(350deg) brightness(95%) contrast(95%)';
            }
        }
        return 'none';
    }};
    }
`;

export const RevenueChartContainer = styled.div`
    width: 100%;
    max-width: 100%;
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 20px;
    padding: ${({ theme }) => theme.spacing.md} 0;
    position: relative;
    overflow-x: visible;
    overflow-y: visible;
    box-sizing: border-box;

    /* Only enable scrolling on small screens */
    @media (max-width: 1024px) {
        overflow-x: auto;
        overflow-y: visible;
        -webkit-overflow-scrolling: touch;
        gap: 12px;
        padding: ${({ theme }) => theme.spacing.sm} 0;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 2px;
        }
    }

    @media (max-width: 768px) {
        gap: 10px;
        padding: ${({ theme }) => theme.spacing.xs} 0;
        min-height: 240px;
    }

    @media (max-width: 480px) {
        gap: 10px;
        padding: ${({ theme }) => theme.spacing.xs} 0;
         min-height: 210px;

    }
`;

export const RevenueChartYAxis = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    padding-right: ${({ theme }) => theme.spacing.sm};
    padding-bottom: 0;
    padding-top: 0;
    font-size: 11px;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#666666';
    }};
    min-width: 35px;
    position: relative;

    @media (max-width: 768px) {
        min-width: 30px;
        font-size: 10px;
        padding-right: ${({ theme }) => theme.spacing.xs};
    }

    @media (max-width: 480px) {
        min-width: 25px;
        font-size: 9px;
    }
`;

export const RevenueChartYAxisLabel = styled.div`
    text-align: right;
    font-size: 14px;
    font-weight: 500;
    color: #A3AED0;
    position: absolute;
    transform: translateY(-50%);

    @media (max-width: 1024px) {
        font-size: 13px;
    }

    @media (max-width: 768px) {
        font-size: 12px;
    }

    @media (max-width: 480px) {
        font-size: 11px;
    }
`;

export const DotChart = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    gap: 4px;
    padding: 0;
    flex: 1;
    min-width: 0;
    overflow: visible;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        gap: 3px;
    }

    @media (max-width: 768px) {
        gap: 2px;
    }

    @media (max-width: 480px) {
        gap: 1px;
    }
`;

export const DotChartColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    height: 100%;
    justify-content: flex-end;
    gap: 1px;
    max-width: 24px;
    min-width: 0;
    position: relative;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        max-width: 22px;
    }

    @media (max-width: 768px) {
        max-width: 20px;
        min-width: 20px;
    }

    @media (max-width: 480px) {
        max-width: 18px;
        min-width: 18px;
    }
`;

export const DotChartDot = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ $filled, theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        if (!$filled) return '#eaeaea';
        return isDark ? '#F04A4A' : '#292929';
    }};
    border: none;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        width: 36px;
        height: 36px;
    }

    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
    }

    @media (max-width: 480px) {
        width: 28px;
        height: 28px;
    }
`;

export const DotChartMonthLabel = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #A3AED0;
    margin-top: 0px;
    white-space: nowrap;

    @media (max-width: 1024px) {
        font-size: 13px;
    }

    @media (max-width: 768px) {
        font-size: 12px;
    }

    @media (max-width: 480px) {
        font-size: 11px;
    }
`;

export const RevenueCountryTitle = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: #ffffff;
    margin-top: 14px;
    margin-bottom: 10px;

    @media (max-width: 1024px) {
        font-size: 22px;
        margin-top: 12px;
        margin-bottom: 8px;
    }

    @media (max-width: 768px) {
        font-size: 20px;
        margin-top: 10px;
        margin-bottom: 8px;
    }

    @media (max-width: 480px) {
        font-size: 18px;
        margin-top: 8px;
        margin-bottom: 6px;
    }
`;

export const CountryChartContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 50px;
    padding: 0 0 60px 0;
    width: 100%;
    margin-top: 0;
    min-height: 0;
    flex: 1;

    @media (max-width: 1024px) {
        gap: 40px;
        padding: 0 0 50px 0;
    }

    @media (max-width: 768px) {
        gap: 30px;
        padding: 0 0 40px 0;
    }

    @media (max-width: 480px) {
        gap: 24px;
        padding: 0 0 30px 0;
    }
`;

export const CountrySpacer = styled.div`
    flex: 1;
    min-height: 0;
`;

export const CountryItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

export const CountryItemTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

export const CountryItemLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const CountryFlag = styled.img`
    width: 18px;
    height: 18px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 16px;
        height: 16px;
    }

    @media (max-width: 480px) {
        width: 14px;
        height: 14px;
    }
`;

export const CountryName = styled.div`
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;

    @media (max-width: 1024px) {
        font-size: 15px;
    }

    @media (max-width: 768px) {
        font-size: 14px;
    }

    @media (max-width: 480px) {
        font-size: 13px;
    }
`;

export const CountryRevenue = styled.div`
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;

    @media (max-width: 1024px) {
        font-size: 15px;
    }

    @media (max-width: 768px) {
        font-size: 14px;
    }

    @media (max-width: 480px) {
        font-size: 13px;
    }
`;

export const CountryProgressBar = styled.div`
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    overflow: hidden;
    position: relative;
`;

export const CountryProgressFill = styled.div`
    height: 100%;
    width: ${({ $width }) => Math.max($width, 2)}%;
    background: #ffffff;
    border-radius: 3px;
    transition: width 0.3s ease;
`;

export const TotalRevenueHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 30px;
    padding-bottom: 0;

    @media (max-width: 1024px) {
        margin-bottom: 24px;
    }

    @media (max-width: 768px) {
        margin-bottom: 20px;
    }

    @media (max-width: 480px) {
        margin-bottom: 16px;
    }
`;

export const TotalRevenueIcon = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    flex-shrink: 0;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#F0F0F0';
    }};
    
    img {
        width: 24px;
        height: 24px;
        object-fit: contain;
        filter: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'none' : 'brightness(0) saturate(100%) invert(25%) sepia(95%) saturate(5000%) hue-rotate(350deg) brightness(95%) contrast(95%)';
    }};
    }

    @media (max-width: 1024px) {
        width: 40px;
        height: 40px;
        padding: 9px;
        
        img {
            width: 22px;
            height: 22px;
        }
    }

    @media (max-width: 768px) {
        width: 36px;
        height: 36px;
        padding: 8px;
        
        img {
            width: 20px;
            height: 20px;
        }
    }

    @media (max-width: 480px) {
        width: 32px;
        height: 32px;
        padding: 7px;
        
        img {
            width: 18px;
            height: 18px;
        }
    }
`;

export const TotalRevenueContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 0;
    padding-top: 0;
    flex: 1;
`;

export const TotalRevenueTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
`;

// Keep old components for backward compatibility
export const CountryBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
`;

export const CountryBarLabel = styled.div`
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    min-width: 90px;
    flex-shrink: 0;
`;

export const CountryBarValue = styled.div`
    background: rgba(255, 255, 255, 0.2);
    height: 32px;
    width: ${({ $width }) => Math.max($width, 8)}%;
    min-width: 60px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 12px;
    color: #ffffff;
    font-weight: 600;
    font-size: 14px;
    border-radius: 4px;
    flex-shrink: 0;
`;

export const DonutChartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${({ theme, $dark }) => $dark ? '14px' : theme.spacing.sm};

    @media (max-width: 768px) {
        margin-bottom: ${({ theme, $dark }) => $dark ? '12px' : theme.spacing.xs};
    }

    @media (max-width: 480px) {
        margin-bottom: ${({ theme, $dark }) => $dark ? '10px' : theme.spacing.xs};
    }
`;

export const DonutChartHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.sm};
`;

export const DonutChartHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    @media (max-width: 768px) {
        gap: 8px;
    }

    @media (max-width: 480px) {
        gap: 6px;
    }
`;

export const DonutChartHeaderIcon = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#ffffff'; // Always white for Top Performing
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 22px;
    flex-shrink: 0;
    border: ${({ $red }) => {
        if ($red) return 'none';
        return '1.5px solid #000000'; // Always black border for Top Performing
    }};

    img {
        width: 16px;
        height: 16px;
        object-fit: contain;
        filter: ${({ $red }) => {
        if ($red) return 'none';
        return 'brightness(0)'; // Always black icon for Top Performing
    }};
    }

    @media (max-width: 1024px) {
        width: 54px;
        height: 54px;
        padding: 20px;
        
        img {
            width: 14px;
            height: 14px;
        }
    }

    @media (max-width: 768px) {
        width: 48px;
        height: 48px;
        padding: 18px;
        
        img {
            width: 12px;
            height: 12px;
        }
    }

    @media (max-width: 480px) {
        width: 42px;
        height: 42px;
        padding: 16px;
        
        img {
            width: 10px;
            height: 10px;
        }
    }
`;

export const DonutChartHeaderButton = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${({ $red }) => {
        if ($red) return 'transparent';
        return '#ffffff'; // Always white for Top Performing
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    color: ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#000000'; // Always black text for Top Performing
    }};
    font-size: 14px;
    font-weight: 500;
    
    .anticon {
        color: ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#000000'; // Always black icon for Top Performing
    }};
        font-size: 24px;
        filter: none;
    }
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.2s;
    border: 1.5px solid ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#000000'; // Always black border for Top Performing
    }};

    &:hover {
        background: ${({ $red }) => {
        if ($red) return 'rgba(255, 255, 255, 0.2)';
        return '#000000'; // Always black on hover for Top Performing
    }};
        border: 1.5px solid ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#000000'; // Always black border on hover for Top Performing
    }};
        color: ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#ffffff'; // Always white text on hover for Top Performing
    }};
        
        .anticon {
            color: ${({ $red }) => {
        if ($red) return '#ffffff';
        return '#ffffff'; // Always white icon on hover for Top Performing
    }};
        }
    }

    @media (max-width: 1024px) {
        width: 40px;
        height: 40px;
        padding: 9px;
        
        .anticon {
            font-size: 22px;
        }
    }

    @media (max-width: 768px) {
        width: 36px;
        height: 36px;
        padding: 8px;
        
        .anticon {
            font-size: 20px;
        }
    }

    @media (max-width: 480px) {
        width: 32px;
        height: 32px;
        padding: 7px;
        
        .anticon {
            font-size: 18px;
        }
    }
`;

export const TableSection = styled.div`
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? theme.colors.background.secondary : '#ffffff';
    }};
    border-radius: 12px;
    padding: ${({ theme }) => theme.spacing.lg};
    border: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return `1px solid ${isDark ? '#2C2C2C' : '#E5E5E5'}`;
    }};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-top: 24px;
`;

export const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.md};
`;

export const TableTitle = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    margin: 0;
    font-family: var(--font-family-lufga);
`;

export const TableActions = styled.div`
    display: flex;
    gap: 10px;
`;

// Subscription Plans Styles
export const PlansOverviewTitle = styled.div`
    font-size: 30px;
    font-weight: 600;
    font-family: var(--font-family-lufga);
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    margin-bottom: 24px;

    @media (max-width: 1024px) {
        font-size: 26px;
        margin-bottom: 20px;
    }

    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 18px;
    }

    @media (max-width: 480px) {
        font-size: 20px;
        margin-bottom: 16px;
    }
`;

export const PlanDetailsCard = styled.div`
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }};
    border-radius: 40px;
    padding: 40px;
    position: relative;
    border: 2px solid #F04A4A;
    margin-top: 0;
    z-index: 1;
`;

export const EditIconButton = styled.button`
    position: absolute;
    top: 40px;
    right: 40px;
    background: transparent;
    border: none; 
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    
    .anticon {
        font-size: 20px;
    }
    
    &:hover {
        opacity: 0.7;
    }
`;

export const PricingSection = styled.div`
    display: flex;
    gap: 106px;
    margin-bottom: 40px;
    flex-wrap: wrap;
`;

export const PricingItem = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

export const PricingSeparator = styled.div`
    width: 1px;
    height: 30px;
    background: #8A98A7;
    flex-shrink: 0;
`;

export const PricingContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const PriceAmount = styled.div`
    font-size: 36px;
    font-weight: 500;
    font-family: var(--font-family-lufga);
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    line-height: 1;
`;

export const PriceLabel = styled.div`
    font-size: 20px;
    font-weight: 400;
    font-family: var(--font-family-lufga);
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#666666';
    }};
`;

export const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

export const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    font-family: var(--font-family-lufga);
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    margin-bottom: 20px;
`;

export const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const FeatureItem = styled.li`
    font-size: 16px;
    font-weight: 400;
    font-family: var(--font-family-lufga);
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    display: flex;
    align-items: flex-start;
    gap: 12px;
    
    &::before {
        content: '';
        width: 3px;
        height: 20px;
        background: #F04A4A;
        border-radius: 2px;
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

